import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Mail, Users } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";

const empty = { name: "", email: "", relation: "family", notes: "", is_child: false };

export default function People() {
  const [people, setPeople] = useState([]);
  const [editing, setEditing] = useState(null); // person_id | null | "new"
  const [form, setForm] = useState(empty);
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/people").then((r) => setPeople(r.data));
  useEffect(() => { load().catch(console.error); }, []);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (p) => { setEditing(p.person_id); setForm({ name: p.name, email: p.email || "", relation: p.relation || "family", notes: p.notes || "", is_child: !!p.is_child }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing === "new") await api.post("/people", form);
      else await api.put(`/people/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const remove = (id) => {
    const person = people.find((p) => p.person_id === id);
    setConfirm({
      title: `Remove ${person?.name || "this person"}?`,
      message: "They'll be removed from your contacts. Bookings that already include them won't be affected.",
      confirmLabel: "Remove",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/people/${id}`);
          toast.success("Removed");
          load();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not remove");
        }
      },
    });
  };

  const family = people.filter((p) => p.relation === "family");
  const friends = people.filter((p) => p.relation === "friend");

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="people-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Familia & amigos</p>
          <h1 className="font-serif-villa text-4xl">Regular travellers</h1>
          <p className="text-sm text-[#7A7A7A] mt-1">Save the people you book most often. Used in the booking form.</p>
        </div>
        <button
          onClick={startNew}
          data-testid="add-person-btn"
          className="villa-btn-primary px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add person
        </button>
      </div>

      {editing && (
        <div className="villa-card p-6" data-testid="person-form">
          <h3 className="font-serif-villa text-xl mb-4">{editing === "new" ? "New person" : "Edit person"}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <input data-testid="person-name" className={inputCls} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="person-email" className={inputCls} type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <select data-testid="person-relation" className={inputCls} value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })}>
              <option value="family">Family</option>
              <option value="friend">Friend</option>
            </select>
            <input data-testid="person-notes" className={inputCls} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <label className="mt-3 inline-flex items-center gap-2 text-sm text-[#2B2B2B] cursor-pointer select-none" data-testid="person-is-child">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[#C25E3E]"
              checked={!!form.is_child}
              onChange={(e) => setForm({ ...form, is_child: e.target.checked })}
            />
            This person is a child
          </label>
          <div className="mt-4 flex items-center gap-2">
            <button onClick={save} data-testid="save-person-btn" className="villa-btn-primary px-5 py-2 rounded-full text-sm">Save</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">Cancel</button>
          </div>
        </div>
      )}

      <Section title="Familia" people={family} onEdit={startEdit} onRemove={remove} />
      <Section title="Amigos" people={friends} onEdit={startEdit} onRemove={remove} />

      {people.length === 0 && (
        <div className="villa-card p-10 text-center">
          <Users className="w-8 h-8 mx-auto text-[#7A7A7A]" strokeWidth={1.4} />
          <p className="font-serif-villa text-2xl mt-3">No one saved yet</p>
          <p className="text-sm text-[#7A7A7A] mt-1">Add your family members to make booking faster.</p>
        </div>
      )}

      <ConfirmModal open={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function Section({ title, people, onEdit, onRemove }) {
  if (!people.length) return null;
  return (
    <section>
      <h2 className="font-serif-villa text-2xl mb-3">{title}</h2>
      <div className="grid md:grid-cols-2 gap-3">
        {people.map((p) => (
          <div key={p.person_id} className="villa-card p-5 flex items-center justify-between gap-3" data-testid={`person-card-${p.person_id}`}>
            <div className="min-w-0">
              <div className="font-serif-villa text-lg flex items-center gap-2">
                {p.name}
                {p.is_self && <span className="villa-chip villa-chip-ok text-[10px]">You</span>}
                {p.is_child && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FBF7EF] border border-[#E8E1D5] text-[#6B705C]">Child</span>}
              </div>
              <div className="text-xs text-[#7A7A7A] flex items-center gap-1 mt-0.5">
                {p.email ? <><Mail className="w-3 h-3" />{p.email}</> : <span className="italic">No email</span>}
              </div>
              {p.notes && <div className="text-xs text-[#9A9A9A] mt-1">{p.notes}</div>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => onEdit(p)} data-testid={`edit-person-${p.person_id}`} className="p-2 rounded-full hover:bg-[#EAE4D8] text-[#7A7A7A]"><Edit3 className="w-4 h-4" /></button>
              {!p.is_self && (
                <button onClick={() => onRemove(p.person_id)} data-testid={`remove-person-${p.person_id}`} className="p-2 rounded-full hover:bg-[#FBE9E7] text-[#A84C30]"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const inputCls = "w-full bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-4 py-2.5 text-[#2B2B2B] placeholder:text-[#B5B5B5] focus:outline-none focus:ring-2 focus:ring-[#C25E3E]/40 focus:border-[#C25E3E] transition-all";
